import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Navbar from "./components/Navbar";
import WhatsAppButton from "./components/WhatsAppButton";
import "./App.css";

import LoadingScreen from "./components/LoadingScreen";
import ConcreteBackground from "./components/ConcreteBackground";
import { EraProvider } from "./context/EraContext";
import { LANG_BASE } from "./i18n";

const Home = lazy(() => import("./pages/Home"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Tab titles for the static routes; detail pages set their own once loaded.
const ROUTE_TITLE_KEYS = {
  "/portfolio": "portfolio",
  "/blog": "blog",
  "/about": "about",
  "/contact": "contact",
};

// BrowserRouter keeps the old scroll offset between routes, so opening a
// project from the bottom of the homepage would land halfway down its page.
// In-page anchors (#work) are left alone.
function RouteEffects() {
  const { pathname, hash } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);

  useEffect(() => {
    const key = ROUTE_TITLE_KEYS[pathname];
    document.title = key ? `${t(key)} — Danillo Otávio Gomes` : t("site_title");
  }, [pathname, t]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/portfolio/:slug" element={<ProjectDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<PostDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <EraProvider>
        <Router basename={LANG_BASE}>
          <RouteEffects />
          <div className="App">
            <ConcreteBackground />
            <Navbar />
            <main>
              <AnimatedRoutes />
            </main>
            <WhatsAppButton />
          </div>
        </Router>
      </EraProvider>
    </Suspense>
  );
}

export default App;
